/*
Página para editar una liquidación existente.
Autor: Benjamín León
*/
import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import useSettlementFetcher from '../hooks/useSettlementFetcher'
import useVehicleFetcher from '../hooks/useVehicleFetcher'
import useClientFetcher from '../hooks/useClientFetcher'
import { useConsignmentFetcher } from '../hooks/useConsignmentFetcher'
import Consignment from '../interfaces/Consignment'
import Settlement from '../interfaces/Settlement'
import Vehicle from '../interfaces/Vehicle'
import Client from '../interfaces/Client'
import ConsignmentDet from '../interfaces/ConsignmentDet'

import { Alert } from './MessageInfo'
import SettlementEdit from './SettlementEdit'

import { AppPath } from '@/enums/AppPath'

const SettlementEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [settlement, setSettlement] = useState<Settlement>()
  const [vehicle, setVehicle] = useState<Vehicle>()
  const [client, setClient] = useState<Client>()
  const [consignmentDet, setConsignmentDet] = useState<ConsignmentDet>()
  const [errorMessage, setErrorMessage] = useState<string>('')

  const { fetchSettlement, errorSettlement } = useSettlementFetcher()
  const { errorVehicle, fetchVehicle } = useVehicleFetcher()
  const { errorClient, fetchClient } = useClientFetcher()
  const { fetchConsignment } = useConsignmentFetcher()

  // Create adapter for ConsignmentDet to Consignment
  const consignmentAdapter: Consignment | undefined = consignmentDet
    ? {
        _id: settlement?.consignment ?? '',
        body: {
          organization: {
            addressComponents: '',
            representatives: '',
            streetNumber: 0,
            legalName: consignmentDet.organization.legalName,
            business: consignmentDet.organization.business,
            address: consignmentDet.organization.address,
            domain: consignmentDet.organization.domain,
            email: consignmentDet.organization.email,
            phone: consignmentDet.organization.phone,
            name: consignmentDet.organization.name,
            nin: consignmentDet.organization.nin,
          },
          vehicle: consignmentDet.vehicle,
          client: {
            firstname: consignmentDet.client.firstname,
            lastname: consignmentDet.client.lastname,
            phone: consignmentDet.client.phone,
            nin: consignmentDet.client.nin,
            address: consignmentDet.client.address,
            email: consignmentDet.client.email,
            gender: 'Masculino',
            streetNumber: 0,
            addressComponents: '',
          },
          profile: settlement?.body.profile || { user: { firstname: '', lastname: '', nin: '' }, email: '' },
        },
        price: consignmentDet.price,
        notes: [consignmentDet.notes],
        minimum: consignmentDet.minimum,
        charge: consignmentDet.charge,
        fee: consignmentDet.fee,
        vehicle: settlement?.vehicle ?? '',
        client: settlement?.client ?? '',
        organization: settlement?.organization ?? '',
        profile: settlement?.profile ?? '',
        city: consignmentDet.city,
        date: new Date(consignmentDet.date),
        createdAt: settlement ? new Date(settlement.createdAt) : new Date(),
        updatedAt: settlement ? new Date(settlement.updatedAt) : new Date(),
        __v: settlement?.__v ?? 0,
        paymentShow: consignmentDet.paymentShow,
        feeIva: consignmentDet.feeIva,
        minimumIva: consignmentDet.minimumIva,
      }
    : undefined

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setErrorMessage('ID is not defined.')

        return
      }

      const settlement = await fetchSettlement(id)

      if (!settlement) {
        setErrorMessage(errorSettlement?.message || 'Error loading settlement')

        return
      }

      setSettlement(settlement)

      // Fetch related consignment
      if (settlement.consignment) {
        const consignmentId = settlement.consignment
        const consignment = await fetchConsignment(consignmentId)

        if (!consignment) {
          setErrorMessage('Error recuperando la consignación')

          return
        }
        setConsignmentDet(consignment)
      }

      // Fetch vehicle
      const vehicleId = settlement.vehicle
      const vehicle = await fetchVehicle(vehicleId)

      if (errorVehicle) {
        setErrorMessage(errorVehicle.message)

        return
      }

      if (!vehicle) {
        setErrorMessage('Error loading vehicle')

        return
      }

      // Fetch client
      const clientId = settlement.client
      const client = await fetchClient(clientId)

      if (errorClient) {
        setErrorMessage(errorClient.message)

        return
      }

      if (!client) {
        setErrorMessage('Error loading client')

        return
      }

      setVehicle(vehicle)
      setClient(client)
    }

    fetchData()
  }, [id])

  const handleRefresh = () => {
    navigate(`/${AppPath.Settlements}/view/${id}`)
  }

  const alertMessageTimeout = (): void => {
    setErrorMessage('')
  }

  if (!settlement || !vehicle || !client || !consignmentAdapter) {
    return (
      <div className="container mx-auto p-4">
        <p>Cargando datos...</p>
        {errorMessage && <Alert message={errorMessage} trigger={Date.now()} onTimeout={alertMessageTimeout} />}
      </div>
    )
  }

  return (
    <div className="container mx-auto p-4">
      {errorMessage && <Alert message={errorMessage} trigger={Date.now()} onTimeout={alertMessageTimeout} />}

      {/* Debug info for development - remove in production */}
      <div className="bg-gray-100 p-2 mb-4 text-xs" style={{ display: 'none' }}>
        <p>Debug: startInEditMode=true is being passed to SettlementEdit</p>
      </div>

      <SettlementEdit
        consignment={consignmentAdapter}
        settlement={settlement}
        onRefresh={handleRefresh}
        startInEditMode={true}
        vehicle={vehicle}
      />
    </div>
  )
}

export default SettlementEditPage
